import { useEffect, useRef, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import unescapedText from 'I18n/utils/unescapedText';
import { useIsPillBorderRadius, useBreakpoint } from 'portals-platform-lib';
import { PAGE_CONTENT_SPACING } from '../constants/layout';
import { usageTracker } from '../utils/usageTracker';
import { InboxNavPanel } from '../components/InboxNavPanel';
import { PanelsContainer } from '../components/CollapsiblePanelStyles';
import { TicketThreadViewerWrapper } from '../components/TicketThreadViewerWrapper';
import { useTicket } from '../data/useTicket';
import { useTicketDetailsParams } from '../hooks/useTicketDetailsParams';
import { usePortalPageTitle } from '../hooks/usePortalPageTitle';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatTicketId } from '../utils/formatTicketId';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function TicketDetails() {
  var _ticket$subject;
  const isMobile = useBreakpoint() === 'mobile';
  const isUsingPillBorderRadius = useIsPillBorderRadius();
  const {
    ticketId
  } = useTicketDetailsParams();
  const {
    data: ticket,
    loading: isTicketLoading
  } = useTicket(ticketId);
  const portalPageTitle = usePortalPageTitle();
  const subject = ticket === null || ticket === void 0 || (_ticket$subject = ticket['subject']) === null || _ticket$subject === void 0 ? void 0 : _ticket$subject.trim();
  useDocumentTitle([subject || formatTicketId(ticketId), portalPageTitle].filter(Boolean).join(' - '));
  const [initialTicketId] = useState(ticketId);
  const hasMovedFocus = useRef(false);
  const isReadyForFocus = ticketId === initialTicketId && !isTicketLoading && !!ticket;
  useEffect(() => {
    if (!isReadyForFocus || hasMovedFocus.current) return undefined;
    const moveFocus = () => {
      if (document.activeElement && document.activeElement !== document.body) {
        hasMovedFocus.current = true;
        return true;
      }
      const focusTarget = document.querySelector('[data-route-focus-target]');
      if (!focusTarget) return false;
      hasMovedFocus.current = true;
      focusTarget.focus({
        preventScroll: true
      });
      return true;
    };
    if (moveFocus()) return undefined;
    const observer = new MutationObserver(() => {
      if (moveFocus()) observer.disconnect();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    const timeoutId = setTimeout(() => observer.disconnect(), 2000);
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [isReadyForFocus]);
  const openedAnnouncement = !isTicketLoading && ticket ? unescapedText('customer-portal-app.ticketDetails.opened', {
    subject: subject || formatTicketId(ticketId)
  }) : '';
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setAnnouncement(openedAnnouncement), 300);
    return () => clearTimeout(id);
  }, [openedAnnouncement]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedDetailsPanelTab, setSelectedDetailsPanelTab] = useState('details');
  useEffect(() => {
    usageTracker.track('Customer Portal App', 'Page Viewed', {
      screen: 'ticket-details'
    });
  }, []);
  const outerFlexProps = isMobile ? {} : {
    borderWidth: '100',
    borderStyle: 'solid',
    borderColor: 'border-core-subtle',
    borderRadius: isUsingPillBorderRadius ? '0' : '300',
    overflow: 'hidden',
    marginBlockEnd: PAGE_CONTENT_SPACING,
    marginInlineStart: PAGE_CONTENT_SPACING,
    marginInlineEnd: PAGE_CONTENT_SPACING
  };
  return /*#__PURE__*/_jsxs(Flex, Object.assign({
    flexGrow: "1"
  }, outerFlexProps, {
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      "aria-live": "polite",
      "aria-atomic": "true",
      children: announcement
    }), /*#__PURE__*/_jsxs(PanelsContainer, {
      children: [!isMobile && /*#__PURE__*/_jsx(InboxNavPanel, {}), /*#__PURE__*/_jsx(TicketThreadViewerWrapper, {
        isPanelOpen: isPanelOpen,
        setIsPanelOpen: setIsPanelOpen,
        selectedDetailsPanelTab: selectedDetailsPanelTab,
        setSelectedDetailsPanelTab: setSelectedDetailsPanelTab
      })]
    })]
  }));
}